/**
 * @fileoverview Inline "Reassign Owner" form for TenantDetailModal.
 * @description Lets the current owner's supervisor (or Super Admin) transfer a target to a
 * different Manager/Executive. The mock API enforces the actual hierarchy-scope rule (see
 * reassignTarget's doc comment) — this form just submits the selection; a 422 from an
 * out-of-scope attempt surfaces as the mutation's own error message.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useReassignTarget } from '@/features/sales/hooks/useReassignTarget'
import {
  reassignOwnerFormSchema,
  type ReassignOwnerFormValues,
} from '@/features/sales/schemas/reassign-owner-form.schema'
import Label from '@/shared/components/atoms/Label'
import Select from '@/shared/components/atoms/Select'

import DialogActions from './DialogActions'

import type { SalesUserRecord } from '@/features/sales/types/tenant.types'
import type { FormEvent } from 'react'

interface ReassignOwnerFormProps {
  tenantId: string
  targetId: string
  currentOwnerId: string | null
  roster: SalesUserRecord[]
  actingUserId: string
  actingUserRole: string
  onUpdated: () => void
  onCancel: () => void
}

/**
 * @component
 * @param {ReassignOwnerFormProps} props - The target to reassign and the acting user's identity.
 * @param {string} props.tenantId - The tenant the target belongs to, for query invalidation.
 * @param {string} props.targetId - The target id to reassign.
 * @param {string | null} props.currentOwnerId - The target's current owner, excluded from the
 * picker's options.
 * @param {SalesUserRecord[]} props.roster - The full sales roster, filtered here to Managers and
 * Executives (a target's owner is never a Sales Head directly — see every spec's "Current Owner:
 * Sales Manager or Executive").
 * @param {string} props.actingUserId - The signed-in user's sales-roster id.
 * @param {string} props.actingUserRole - The signed-in user's active role name.
 * @param {() => void} props.onUpdated - Called after a successful reassignment.
 * @param {() => void} props.onCancel - Called to dismiss the form without reassigning.
 */
function ReassignOwnerForm({
  tenantId,
  targetId,
  currentOwnerId,
  roster,
  actingUserId,
  actingUserRole,
  onUpdated,
  onCancel,
}: ReassignOwnerFormProps) {
  const { t } = useTranslation()
  const reassignTarget = useReassignTarget(tenantId)
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ReassignOwnerFormValues>({
    resolver: zodResolver(reassignOwnerFormSchema),
    defaultValues: { toOwnerId: '' },
  })

  const managers = roster.filter(
    (user) => user.role === 'Sales Manager' && user.id !== currentOwnerId,
  )
  const executives = roster.filter(
    (user) => user.role === 'Sales Executive' && user.id !== currentOwnerId,
  )

  /**
   * @description Submits the reassignment.
   * @param {ReassignOwnerFormValues} values - The validated form values.
   */
  const onValid = (values: ReassignOwnerFormValues) => {
    reassignTarget.mutate(
      { targetId, input: { toOwnerId: values.toOwnerId, actingUserId, actingUserRole } },
      { onSuccess: onUpdated },
    )
  }

  /**
   * @description Runs the schema validation and, if valid, calls onValid.
   * @param {FormEvent<HTMLFormElement>} event - The form submit event.
   */
  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    void handleSubmit(onValid)(event)
  }

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      data-testid="reassign-owner-form"
      className="flex flex-col gap-3 rounded-lg border border-row-border bg-surface-hover p-3.5"
    >
      <span className="text-[12.5px] font-bold text-ink" data-testid="reassign-owner-title">
        {t('sales.tenantDetail.reassign.title')}
      </span>

      <div>
        <Label htmlFor="reassignOwner">{t('sales.tenantDetail.reassign.label')}</Label>
        <div className="mt-1.5">
          <Select
            id="reassignOwner"
            data-testid="reassign-owner-select"
            invalid={Boolean(errors.toOwnerId)}
            options={[
              { value: '', label: t('sales.pipeline.form.selectPlaceholder') },
              ...managers.map((user) => ({ value: user.id, label: `${user.name} · Manager` })),
              ...executives.map((user) => ({ value: user.id, label: `${user.name} · Executive` })),
            ]}
            {...register('toOwnerId')}
          />
        </div>
        {errors.toOwnerId && (
          <span
            className="mt-1 block text-[11.5px] font-medium text-danger"
            data-testid="reassign-owner-error"
          >
            {t(errors.toOwnerId.message ?? '')}
          </span>
        )}
      </div>

      <DialogActions
        testIdPrefix="reassign-owner"
        cancelLabel={t('sales.pipeline.form.cancel')}
        confirmLabel={t('sales.tenantDetail.reassign.submit')}
        onCancel={onCancel}
        confirmType="submit"
        isLoading={reassignTarget.isPending}
        disabled={false}
      />

      {reassignTarget.isError && (
        <p
          role="alert"
          className="text-[12.5px] text-danger"
          data-testid="reassign-owner-request-error"
        >
          {reassignTarget.error instanceof Error
            ? reassignTarget.error.message
            : String(reassignTarget.error)}
        </p>
      )}
    </form>
  )
}

export default ReassignOwnerForm
